/**
 * PeekOverlay.jsx — a frame (or two, to compare) large. Shows the thumbnail at
 * once, then swaps in the full-resolution original when it has decoded.
 */

import { useEffect, useState } from 'react';
import {
  closePeek, selectImgById, selectVisible, stepPeek, toggleSel, updatePhotoDims, useCarousel,
} from '../../hooks/useCarouselStore';
import { filterString, normalizeLook } from '../../lib/carousel/look.js';

function PeekImage({ im }) {
  const [full, setFull] = useState('');

  useEffect(() => {
    let live = true;
    setFull('');
    const img = new Image();
    let tried = false;
    img.onload = () => {
      if (!live) return;
      setFull(img.src);
      updatePhotoDims(im.id, img.naturalWidth, img.naturalHeight);
    };
    img.onerror = () => {
      // a shoot's delivery URL rotates; the portal can still fetch it
      if (!live || tried || !im.fallbackUrl) return;
      tried = true;
      img.src = im.fallbackUrl;
    };
    img.src = im.url;
    return () => {
      live = false;
      img.onload = null;
      img.onerror = null;
      img.removeAttribute('src');
    };
  }, [im.id, im.url, im.fallbackUrl]);

  const look = normalizeLook(im.look);
  const style = {};
  if (look.rot) style.transform = 'rotate(' + look.rot + 'deg)';
  const f = filterString(look.adj);
  if (f) style.filter = f;
  return <img className="peekimg" alt={im.name} src={full || im.thumb} style={style} />;
}

export default function PeekOverlay() {
  const peek = useCarousel((s) => s.peek);
  const sel = useCarousel((s) => s.sel);
  const imgById = useCarousel(selectImgById);
  const visible = useCarousel(selectVisible);
  if (!peek) return null;

  const single = peek.length === 1;
  const names = peek.map((id) => { const im = imgById(id); return im ? im.name : '?'; });
  const i = single ? visible.findIndex((im) => im.id === peek[0]) : -1;
  const s = single ? sel.indexOf(peek[0]) : -1;

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget || e.target.classList.contains('peekstage')) closePeek();
  };

  return (
    <div className="peek" style={{ '--n': String(peek.length) }} onClick={onBackdrop}>
      <div className="peekstage">
        {peek.map((id) => { const im = imgById(id); return im ? <PeekImage key={id} im={im} /> : null; })}
      </div>
      <div className="peek-bar">
        <span className="mono">{names.join('   vs   ')}</span>
        <span className="lbl">{single && i >= 0 ? (i + 1) + ' of ' + visible.length : ''}</span>
        <span className="spacer" />
        <button type="button" className="btn tiny" disabled={!single} onClick={() => toggleSel(peek[0])}>
          {single ? (s >= 0 ? 'Selected ' + (s + 1) : 'Select') : 'Comparing'}
        </button>
        <button type="button" className="btn tiny ghost" title="Previous frame" aria-label="Previous frame" disabled={!single} onClick={() => stepPeek(-1)}>←</button>
        <button type="button" className="btn tiny ghost" title="Next frame" aria-label="Next frame" disabled={!single} onClick={() => stepPeek(1)}>→</button>
        <button type="button" className="btn tiny ghost" onClick={closePeek}>Close</button>
      </div>
    </div>
  );
}
